import { decimateIndices, maxOf, minOf, type DecimateAxis } from "@heraion/shared";

import type { ChartSeries, ChartSeriesAnnotation, ChartVariant } from "./chartAnnotations";
import type { LineStroke } from "./chartDots";

// Pure model for `WagmiLineChart.tsx`, no React, no wagmi import. PG-42
// (real-sample downsampling, `decimateBuiltSeries` below) runs on the
// ANNOTATED series, on the SAME axis this file computes (`wagmiAxisRange`,
// below): one function, not a second copy of the axis math.

// Same value `WagmiPathLayers.tsx`, `WagmiChartDots.tsx`'s `pxY` and the tap
// layer's marker all read (passed down from `WagmiLineChart.tsx`, one
// constant, never two copies that could disagree).
export const WAGMI_Y_GUTTER = 10;

// wagmi's `<LineChart>` reserves a FIXED 40px under its own `height` for an
// x-axis cursor label we never render (`WagmiLineChart.tsx`'s header). Each
// layer mounts at `height + WAGMI_BOTTOM_RESERVED` and is clipped back.
export const WAGMI_BOTTOM_RESERVED = 40;

// One layer is one SERIES, one wagmi instance, one continuous path. It was
// one per piece of curve until PG-36, which is where the cost came from.
export type Layer = {
  id: string;
  variant: ChartVariant;
  stroke?: LineStroke;
  current: boolean;
  stale: boolean;
};

export type WagmiChartData = Record<
  string,
  {
    timestamp: number;
    value: number;
    /** True when this KEPT point carries a real `gapDays` label
     *  (`ChartDot.gapDays`, `chartAnnotations.ts`), so `WagmiChartDots.tsx`
     *  never hides the one dot that says "N days since the previous point"
     *  on a dense series (Antenore, 25/09). Derived straight from
     *  `built[].dots` at construction, in `buildWagmiChartModel` below,
     *  never a second index-keyed source that could drift from it. */
    hasGap?: boolean;
  }[]
>;

export type WagmiChartModel = {
  isEmpty: boolean;
  lo: number;
  hi: number;
  x0: number;
  xEnd: number;
  data: WagmiChartData;
  layers: Layer[];
};

// ONE time axis for every series (a dashed companion curve must line up
// with the solid one it annotates): oldest sample → now. Ending the axis at
// `now` rather than at the newest sample is what makes a dead feed visible.
// PG-42: extracted so `WagmiLineChart.tsx` can compute the SAME axis before
// `buildWagmiChartModel` runs, to bucket `decimateBuiltSeries` on it. ⛔
// Never a second copy of this math.
export function wagmiAxisRange(
  series: ChartSeries[],
  now: number,
): Pick<WagmiChartModel, "x0" | "xEnd"> {
  const allXs = series.flatMap((s) => s.points.map((p) => p.x));
  const x0 = allXs.length > 0 ? minOf(allXs) : now;
  const xEnd = Math.max(now, maxOf(allXs));
  return { x0, xEnd };
}

export type DecimatedBuilt = {
  built: ChartSeriesAnnotation[];
  /** Per series key, the RAW point index each KEPT position maps back to,
   *  ascending: `keptIdx[key][j]` is where `built[j]` (the j-th kept point)
   *  sat in the series before decimation. `WagmiLineChart.tsx` uses this to
   *  map a tap on a kept point back to the index the host's selection
   *  holds, which counts the RAW points. */
  keptIdx: Record<string, number[]>;
};

// PG-42, 24/09 review fix: decimating BEFORE `annotateChartSeries` made
// `cadenceMs`/`freshness`/`isGap` (`chartAnnotations.ts:118-160`) read the
// gap between KEPT points, not real neighbours: a stale series could read
// as current, and a wide column spanning days could invent a `gapDays` that
// never happened. So this decimates the ALREADY-ANNOTATED series instead:
// one set of kept indices per series (`decimateIndices`, `@heraion/shared`),
// applied to `points` and `dots` together (they are parallel arrays, one
// dot per point), so `gapDays`/`last`/`stale`/`current`/`headline`/`name`
// all stay exactly what `annotateChartSeries` computed from the TRUE
// neighbours. `columns <= 0` (width 0 before `onLayout`): passthrough.
//
// 25/09 review: a point whose `dots[i].gapDays` is defined (a REAL silence,
// >=1 day) is ALWAYS kept, unioned with `decimateIndices`' own choice, even
// when its own bucket already picked 4 other samples. A wide column can
// span days on a long window, and the one sample that SAYS "N days since
// the previous point" is exactly the one a bucket vote has no reason to
// prefer over its neighbours: a silence must never vanish from the a11y
// reading or the drawing just because decimation could not see it as an
// extreme.
export function decimateBuiltSeries(built: ChartSeriesAnnotation[], axis: DecimateAxis): DecimatedBuilt {
  if (axis.columns <= 0) {
    const keptIdx: Record<string, number[]> = {};
    built.forEach((b) => {
      keptIdx[b.key] = b.points.map((_, i) => i);
    });
    return { built, keptIdx };
  }
  const keptIdx: Record<string, number[]> = {};
  const decimated = built.map((b) => {
    const kept = new Set(decimateIndices(b.points, axis));
    b.dots.forEach((d, i) => {
      if (d.gapDays !== undefined) kept.add(i);
    });
    const idxs = [...kept].sort((a, c) => a - c);
    keptIdx[b.key] = idxs;
    return { ...b, points: idxs.map((i) => b.points[i]!), dots: idxs.map((i) => b.dots[i]!) };
  });
  return { built: decimated, keptIdx };
}

/** From `series` (for `isEmpty`/`lo`/`hi`), `built` (for `data`/`layers`) and
 *  a precomputed `axis` (25/09 review: `wagmiAxisRange` is called ONCE per
 *  render, by `WagmiLineChart.tsx`, and shared with the decimation step
 *  rather than re-derived here), everything `WagmiLineChart` needs to draw. */
export function buildWagmiChartModel(
  series: ChartSeries[],
  built: ChartSeriesAnnotation[],
  axis: Pick<WagmiChartModel, "x0" | "xEnd">,
): WagmiChartModel {
  const allYs = series.flatMap((s) => s.points.map((p) => p.y));
  const isEmpty = allYs.length === 0;
  let lo = isEmpty ? 0 : minOf(allYs);
  let hi = isEmpty ? 0 : maxOf(allYs);
  if (lo === hi) {
    lo -= 1;
    hi += 1;
  }

  const { x0, xEnd } = axis;

  // Z-ORDER ONLY: which layer paints on top (callers still push
  // compare/secondary first, current last, so it stays visually on top). It
  // does not decide which series a tap reads, `WagmiLineChart` reads the
  // DECLARED `headline` flag for that (`resolveHeadlineIndex`).
  const data: WagmiChartData = {};
  const layers: Layer[] = [];
  built.forEach((b) => {
    data[b.key] = b.points.map((p, i) => ({
      timestamp: p.x,
      value: p.y,
      hasGap: b.dots[i]?.gapDays !== undefined,
    }));
    layers.push({
      id: b.key,
      variant: b.variant,
      ...(b.stroke ? { stroke: b.stroke } : {}),
      current: b.current,
      stale: b.stale,
    });
  });

  return { isEmpty, lo, hi, x0, xEnd, data, layers };
}

/** Same domain→range mapping as wagmi's own `getPath`, see
 *  `WagmiChartDots.tsx`'s header for why it's reproduced here instead of
 *  using `<LineChart.Dot>`. x maps `[x0, xEnd] → [0, width]`, y maps
 *  `[lo, hi] → [height - gutter, gutter]`. */
export function wagmiPixelMapper(
  { x0, xEnd, lo, hi }: Pick<WagmiChartModel, "x0" | "xEnd" | "lo" | "hi">,
  width: number,
  height: number,
  yGutter = WAGMI_Y_GUTTER,
) {
  const xSpan = xEnd - x0 || 1;
  const ySpan = hi - lo; // never 0: padded above when lo === hi
  const pxX = (x: number) => ((x - x0) / xSpan) * width;
  const pxY = (y: number) =>
    height - yGutter - ((y - lo) / ySpan) * (height - 2 * yGutter);
  return { pxX, pxY };
}
